import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, 
  Medal, 
  Target,
  TrendingUp,
  Calendar,
  Users,
  ChevronUp,
  ChevronDown,
  Minus,
  Flame,
  Crown,
  Dumbbell,
  Star
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const MatrixLeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekInfo, setWeekInfo] = useState({ start: '', end: '' });
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts/matrix/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
      setWeekInfo({ 
        start: response.data.week_start, 
        end: response.data.week_end 
      });
      setCurrentUserRank(response.data.current_user);
      setTotalParticipants(response.data.total_participants || 0);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-lg font-bold text-aura-text-muted">#{rank}</span>;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-gradient-to-r from-green-500 to-emerald-400';
    if (percentage >= 60) return 'bg-gradient-to-r from-blue-500 to-cyan-400';
    if (percentage >= 40) return 'bg-gradient-to-r from-yellow-500 to-orange-400';
    return 'bg-gradient-to-r from-red-500 to-pink-400';
  };

  return (
    <DashboardLayout title="Training Leaderboard">
      <div className="px-4 py-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-wider">
              Performance Leaderboard
            </h1>
          </div>
          <p className="text-aura-text-muted">This is how athletes separate themselves.</p>
          {weekInfo.start && (
            <Badge className="mt-2 bg-white/10 text-white">
              <Calendar className="w-3 h-3 mr-1" />
              {format(parseISO(weekInfo.start), 'MMM d')} - {format(parseISO(weekInfo.end), 'MMM d, yyyy')}
            </Badge>
          )}
        </div>

        {/* Current User Card (if not in top 3) */}
        {currentUserRank && currentUserRank.rank > 3 && (
          <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-aura-primary/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-aura-primary/30 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">#{currentUserRank.rank}</span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white">Your Position</p>
                    <p className="text-sm text-aura-text-muted">
                      {currentUserRank.completed_count} drills • {currentUserRank.total_points} pts
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-heading font-bold text-aura-primary">
                    {currentUserRank.completion_percentage}%
                  </div>
                  <p className="text-xs text-aura-text-muted">completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-2 mb-6">
            {/* 2nd Place */}
            <div className="order-1">
              <Card className={`bg-gradient-to-b from-gray-400/20 to-gray-500/10 border-gray-400/30 h-full ${
                leaderboard[1]?.is_current_user ? 'ring-2 ring-aura-primary' : ''
              }`}>
                <CardContent className="p-4 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-gray-800">2</span>
                  </div>
                  <p className="font-heading font-bold text-white text-sm truncate">
                    {leaderboard[1]?.name || 'TBD'}
                  </p>
                  <p className="text-xs text-aura-text-muted truncate">
                    {leaderboard[1]?.sport || 'Athlete'}
                  </p>
                  <div className="mt-2">
                    <span className="text-xl font-bold text-gray-300">
                      {leaderboard[1]?.completion_percentage || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    {leaderboard[1]?.total_points || 0} pts
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* 1st Place */}
            <div className="order-2 -mt-4">
              <Card className={`bg-gradient-to-b from-yellow-500/30 to-amber-500/10 border-yellow-500/50 ${
                leaderboard[0]?.is_current_user ? 'ring-2 ring-aura-primary' : ''
              }`}>
                <CardContent className="p-4 text-center">
                  <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-1" />
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mb-2 shadow-lg shadow-yellow-500/30">
                    <span className="text-3xl font-bold text-yellow-900">1</span>
                  </div>
                  <p className="font-heading font-bold text-white truncate">
                    {leaderboard[0]?.name || 'TBD'}
                  </p>
                  <p className="text-xs text-aura-text-muted truncate">
                    {leaderboard[0]?.sport || 'Athlete'}
                  </p>
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-yellow-400">
                      {leaderboard[0]?.completion_percentage || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    {leaderboard[0]?.total_points || 0} pts
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* 3rd Place */}
            <div className="order-3">
              <Card className={`bg-gradient-to-b from-amber-700/20 to-amber-800/10 border-amber-700/30 h-full ${
                leaderboard[2]?.is_current_user ? 'ring-2 ring-aura-primary' : ''
              }`}>
                <CardContent className="p-4 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-amber-200">3</span>
                  </div>
                  <p className="font-heading font-bold text-white text-sm truncate">
                    {leaderboard[2]?.name || 'TBD'}
                  </p>
                  <p className="text-xs text-aura-text-muted truncate">
                    {leaderboard[2]?.sport || 'Athlete'}
                  </p>
                  <div className="mt-2">
                    <span className="text-xl font-bold text-amber-500">
                      {leaderboard[2]?.completion_percentage || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    {leaderboard[2]?.total_points || 0} pts
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-aura-primary mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">{totalParticipants}</p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Participants</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Dumbbell className="w-6 h-6 text-aura-success mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {leaderboard.reduce((sum, e) => sum + e.completed_count, 0)}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Total Drills</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <Star className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold text-white">
                {leaderboard.reduce((sum, e) => sum + e.total_points, 0)}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Total Points</p>
            </CardContent>
          </Card>
        </div>

        {/* Full Leaderboard */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-aura-primary" />
              Full Rankings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <p className="text-aura-text-muted mb-2">No rankings yet this week</p>
                <p className="text-sm text-aura-text-subtle">
                  Complete drills in the Training Matrix to join the leaderboard!
                </p>
                <Button 
                  className="mt-4 bg-aura-primary hover:bg-aura-primary-hover text-white"
                  onClick={() => window.location.href = '/workouts'}
                >
                  <Dumbbell className="w-4 h-4 mr-2" />
                  Go to Training
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.slice(3).map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                      entry.is_current_user 
                        ? 'bg-aura-primary/20 border border-aura-primary/50' 
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`rank-${entry.rank}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankDisplay(entry.rank)}
                      </div>
                      <div>
                        <p className={`font-medium ${entry.is_current_user ? 'text-aura-primary' : 'text-white'}`}>
                          {entry.name}
                          {entry.is_current_user && <span className="ml-2 text-xs">(You)</span>}
                        </p>
                        <p className="text-xs text-aura-text-muted">
                          {entry.sport || 'Athlete'} • {entry.completed_count} drills
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(entry.completion_percentage)} transition-all`}
                          style={{ width: `${entry.completion_percentage}%` }}
                        />
                      </div>
                      <div className="text-right min-w-[60px]">
                        <p className="font-heading font-bold text-white">
                          {entry.completion_percentage}%
                        </p>
                        <p className="text-xs text-aura-text-muted">{entry.total_points} pts</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Motivational CTA */}
        {currentUserRank && currentUserRank.rank > 1 && (
          <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/30">
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <p className="text-white font-medium mb-1">
                {currentUserRank.rank <= 5 
                  ? "You're so close to the top! Keep pushing!" 
                  : "Every drill counts! Complete more to climb the ranks."}
              </p>
              <p className="text-sm text-aura-text-muted">
                {currentUserRank.rank - 1} spot{currentUserRank.rank > 2 ? 's' : ''} away from #{currentUserRank.rank - 1}
              </p>
              <Button 
                className="mt-3 bg-purple-500 hover:bg-purple-600 text-white"
                onClick={() => window.location.href = '/workouts'}
              >
                Train Now
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MatrixLeaderboardPage;
